import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  Clock,
  Eye,
  Megaphone,
  Repeat,
  Send,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HasAccess } from "@/components/common/has-access";
import useRegions from "@/features/regions/hooks/use-regions";
import { MultiSelect } from "./multi-select";
import { UserSearchSelect, PickedUser } from "./user-search-select";
import { RecipientsPreviewDialog } from "./recipients-preview-dialog";
import { LifecycleTiles } from "./lifecycle-tiles";
import { DeepLinkSelect } from "./deep-link-select";
import {
  useBroadcastPreview,
  useSendBroadcast,
  useCreateScheduledBroadcast,
} from "../hooks/use-broadcast";
import {
  BroadcastFilters,
  BroadcastFrequency,
  BroadcastRecurrence,
  CreateScheduledBroadcastInput,
  LifecycleSegment,
  TestUserMode,
} from "../services/broadcast";

const RISK_OPTIONS = [
  { id: "LOW_RISK", label: "Low Risk" },
  { id: "MEDIUM_RISK", label: "Medium Risk" },
  { id: "HIGH_RISK", label: "High Risk" },
];
const BEHAVIOR_TAG_OPTIONS = [
  { id: "TOUGH_USER", label: "Tough User" },
  { id: "BAD_EXPERIENCE_FROM_RH", label: "Bad Experience from RH" },
  { id: "REFUND_SENSITIVE", label: "Refund Sensitive" },
  { id: "CHANGE_SENSITIVE", label: "Change Sensitive" },
];
const TIER_OPTIONS = [
  { id: "Diamond", label: "Diamond" },
  { id: "Platinum", label: "Platinum" },
  { id: "Gold", label: "Gold" },
  { id: "Silver", label: "Silver" },
  { id: "Single", label: "Single" },
];
const STATUS_OPTIONS = [
  { id: "NOT_INITIATED", label: "Not Initiated" },
  { id: "PENDING", label: "Pending" },
  { id: "APPROVED", label: "Approved" },
  { id: "REJECTED", label: "Rejected" },
];
const USER_TYPE_OPTIONS = [
  { id: "DIRECT", label: "Direct" },
  { id: "B2B", label: "B2B" },
  { id: "B2B2C", label: "B2B2C" },
];
const WEEKDAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const DEFAULT_TYPE = "appointment_reminder";
const DEFAULT_PATH = "/appointments";
const ANY_REGION = "ALL";

type SendMode = "NOW" | "SCHEDULE" | "RECURRING";

/** datetime-local input value for a Date, in the browser's local time. */
function toLocalInput(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

/** Drop empty arrays / blank region so the request carries only active filters. */
function cleanFilters(filters: BroadcastFilters): BroadcastFilters {
  const out: BroadcastFilters = {};
  (Object.keys(filters) as (keyof BroadcastFilters)[]).forEach((key) => {
    const value = filters[key];
    if (Array.isArray(value)) {
      if (value.length) (out as any)[key] = value;
    } else if (value) {
      (out as any)[key] = value;
    }
  });
  return out;
}

function useDebounced<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function BroadcastForm() {
  const [filters, setFilters] = useState<BroadcastFilters>({});
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState(DEFAULT_TYPE);
  const [path, setPath] = useState(DEFAULT_PATH);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Audience extras
  const [lifecycle, setLifecycle] = useState<LifecycleSegment[]>([]);
  const [testUsers, setTestUsers] = useState<TestUserMode>("EXCLUDE");
  const [pickedUsers, setPickedUsers] = useState<PickedUser[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Delivery
  const [sendMode, setSendMode] = useState<SendMode>("NOW");
  const [scheduledAt, setScheduledAt] = useState("");
  const [frequency, setFrequency] = useState<BroadcastFrequency>("DAILY");
  const [time, setTime] = useState("10:00");
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [endAt, setEndAt] = useState("");

  const nowLocal = toLocalInput(new Date());

  const { data: regionsData } = useRegions(0);
  const regions = regionsData?.regions ?? [];

  // Full audience payload: filters + lifecycle + test-user mode + hand-picked ids.
  const audience = useMemo(() => {
    const a = cleanFilters(filters);
    if (lifecycle.length) a.lifecycle = lifecycle;
    if (testUsers !== "EXCLUDE") a.test_users = testUsers;
    if (pickedUsers.length) a.user_ids = pickedUsers.map((u) => u.id);
    return a;
  }, [filters, lifecycle, testUsers, pickedUsers]);
  // Segment-tile counts are scoped by everything EXCEPT the lifecycle selection.
  const countsFilters = useMemo(() => {
    const { lifecycle: _omit, ...rest } = audience;
    return rest;
  }, [audience]);
  const debouncedCountsFilters = useDebounced(countsFilters);
  const debouncedFilters = useDebounced(audience);
  const { data: preview, isFetching: previewLoading } =
    useBroadcastPreview(debouncedFilters);
  const matchedUsers = preview?.matchedUsers ?? 0;

  const { mutate: send, isPending: sending } = useSendBroadcast();
  const { mutate: schedule, isPending: scheduling } =
    useCreateScheduledBroadcast();
  const submitting = sending || scheduling;

  const setArray = (key: keyof BroadcastFilters) => (next: string[]) =>
    setFilters((f) => ({ ...f, [key]: next }));

  const activeFilterCount = Object.keys(audience).length;
  const messageReady =
    !!title.trim() && !!body.trim() && !!type.trim() && !!path.trim();

  const scheduleValid =
    !!scheduledAt && new Date(scheduledAt).getTime() > Date.now();
  const recurrenceValid =
    !!time &&
    (frequency !== "WEEKLY" || weekdays.length > 0) &&
    (frequency !== "MONTHLY" || (dayOfMonth >= 1 && dayOfMonth <= 31));

  const deliveryValid =
    sendMode === "NOW"
      ? matchedUsers > 0
      : sendMode === "SCHEDULE"
        ? scheduleValid
        : recurrenceValid;

  const canSubmit = messageReady && deliveryValid && !submitting;

  const resetFilters = () => {
    setFilters({});
    setLifecycle([]);
    setTestUsers("EXCLUDE");
    setPickedUsers([]);
  };
  const resetMessage = () => {
    setTitle("");
    setBody("");
    setType(DEFAULT_TYPE);
    setPath(DEFAULT_PATH);
  };

  const toggleWeekday = (value: number) =>
    setWeekdays((w) =>
      w.includes(value) ? w.filter((d) => d !== value) : [...w, value].sort(),
    );

  const describeRecurrence = () => {
    if (frequency === "DAILY") return `every day at ${time} IST`;
    if (frequency === "WEEKLY") {
      const names = WEEKDAYS.filter((d) => weekdays.includes(d.value))
        .map((d) => d.label)
        .join(", ");
      return `every ${names || "…"} at ${time} IST`;
    }
    return `on day ${dayOfMonth} of each month at ${time} IST`;
  };

  const handleConfirm = () => {
    const base = {
      ...audience,
      title: title.trim(),
      body: body.trim(),
      type: type.trim(),
      path: path.trim(),
    };

    if (sendMode === "NOW") {
      send(base, {
        onSuccess: () => {
          setConfirmOpen(false);
          resetMessage();
        },
      });
      return;
    }

    let payload: CreateScheduledBroadcastInput;
    if (sendMode === "SCHEDULE") {
      payload = {
        ...base,
        kind: "ONCE",
        scheduled_at: new Date(scheduledAt).toISOString(),
      };
    } else {
      const recurrence: BroadcastRecurrence = { frequency, time };
      if (frequency === "WEEKLY") recurrence.weekdays = weekdays;
      if (frequency === "MONTHLY") recurrence.day_of_month = dayOfMonth;
      payload = {
        ...base,
        kind: "RECURRING",
        recurrence,
        ...(endAt ? { end_at: new Date(endAt).toISOString() } : {}),
      };
    }

    schedule(payload, {
      onSuccess: () => {
        setConfirmOpen(false);
        resetMessage();
      },
    });
  };

  const modeButton = (mode: SendMode, label: string) => (
    <Button
      type="button"
      variant={sendMode === mode ? "default" : "outline"}
      className={sendMode === mode ? "bg-gray-900 text-white" : ""}
      onClick={() => setSendMode(mode)}
    >
      {label}
    </Button>
  );

  const primaryLabel =
    sendMode === "NOW"
      ? `Broadcast to ${matchedUsers.toLocaleString()} user${matchedUsers === 1 ? "" : "s"}`
      : sendMode === "SCHEDULE"
        ? "Schedule broadcast"
        : "Create recurring broadcast";

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Audience */}
        <section className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <Users className="h-4 w-4" /> Audience
            </h2>
            {activeFilterCount > 0 && (
              <button
                onClick={resetFilters}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Lifecycle segments — the primary audience control */}
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            Lifecycle segment
          </div>
          <LifecycleTiles
            selected={lifecycle}
            onChange={setLifecycle}
            countsFilters={debouncedCountsFilters}
          />

          {/* Advanced filters — the legacy dropdowns, collapsed by default */}
          <button
            type="button"
            onClick={() => setAdvancedOpen((o) => !o)}
            className="mt-5 flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Advanced filters
            <ChevronDown
              className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`}
            />
          </button>

          {advancedOpen && (
          <div className="mt-3 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <MultiSelect
              label="Risk category"
              options={RISK_OPTIONS}
              selected={filters.risk_category ?? []}
              onChange={setArray("risk_category")}
            />
            <MultiSelect
              label="Tier"
              options={TIER_OPTIONS}
              selected={filters.tiers ?? []}
              onChange={setArray("tiers")}
            />
            <MultiSelect
              label="Behavior tags"
              options={BEHAVIOR_TAG_OPTIONS}
              selected={filters.behavior_tags ?? []}
              onChange={setArray("behavior_tags")}
            />
            <MultiSelect
              label="User type"
              options={USER_TYPE_OPTIONS}
              selected={filters.user_type ?? []}
              onChange={setArray("user_type")}
            />
            <MultiSelect
              label="HRC status"
              options={STATUS_OPTIONS}
              selected={filters.hrc_status ?? []}
              onChange={setArray("hrc_status")}
            />
            <MultiSelect
              label="MC status"
              options={STATUS_OPTIONS}
              selected={filters.mc_status ?? []}
              onChange={setArray("mc_status")}
            />
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Region</label>
              <Select
                value={filters.region ?? ANY_REGION}
                onValueChange={(v) =>
                  setFilters((f) => ({
                    ...f,
                    region: v === ANY_REGION ? undefined : v,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_REGION}>Any region</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={r.code}>
                      {r.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                Test users
              </label>
              <Select
                value={testUsers}
                onValueChange={(v) => setTestUsers(v as TestUserMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EXCLUDE">Excluded</SelectItem>
                  <SelectItem value="INCLUDE">Included</SelectItem>
                  <SelectItem value="ONLY">Only test users</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

            <UserSearchSelect picked={pickedUsers} onChange={setPickedUsers} />
          </div>
          )}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-md bg-orange-50 px-4 py-3 text-sm text-orange-800">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {previewLoading ? (
                <span>Estimating recipients…</span>
              ) : (
                <span>
                  <span className="font-semibold">
                    {matchedUsers.toLocaleString()}
                  </span>{" "}
                  matching user{matchedUsers === 1 ? "" : "s"}
                  {activeFilterCount === 0 ? " (all users)" : ""}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              disabled={matchedUsers === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-orange-200 bg-white px-2.5 py-1 text-xs font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-50"
            >
              <Eye className="h-3.5 w-3.5" /> Preview recipients
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Only users who installed the app receive the push, so devices reached
            may be fewer than the matched count.
            {sendMode !== "NOW" &&
              " Scheduled/recurring sends re-check the audience at each run."}
          </p>
        </section>

        {/* Message */}
        <section className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800">
            <Megaphone className="h-4 w-4" /> Message
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bc-title">Title</Label>
              <Input
                id="bc-title"
                placeholder="A note from Rocket Health"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bc-body">Message</Label>
              <Textarea
                id="bc-body"
                placeholder="Tap to open the app."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="bc-type">Type</Label>
                <Input
                  id="bc-type"
                  placeholder={DEFAULT_TYPE}
                  value={type}
                  onChange={(e) =>
                    setType(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_"))
                  }
                  onBlur={() => setType((t) => t.replace(/^_+|_+$/g, ""))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bc-path">Deep-link path</Label>
                <DeepLinkSelect
                  value={path}
                  onChange={setPath}
                  regionCode={filters.region}
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Delivery */}
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-800">
          <Clock className="h-4 w-4" /> Delivery
        </h2>

        <div className="flex flex-wrap gap-2">
          {modeButton("NOW", "Send now")}
          {modeButton("SCHEDULE", "Schedule")}
          {modeButton("RECURRING", "Recurring")}
        </div>

        {sendMode === "SCHEDULE" && (
          <div className="mt-4 max-w-xs space-y-2">
            <Label htmlFor="bc-when">Send at (your local time)</Label>
            <Input
              id="bc-when"
              type="datetime-local"
              min={nowLocal}
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
            {scheduledAt && !scheduleValid && (
              <p className="text-xs text-red-500">Pick a future time.</p>
            )}
          </div>
        )}

        {sendMode === "RECURRING" && (
          <div className="mt-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={frequency}
                  onValueChange={(v) => setFrequency(v as BroadcastFrequency)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DAILY">Daily</SelectItem>
                    <SelectItem value="WEEKLY">Weekly</SelectItem>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bc-time">Time (IST)</Label>
                <Input
                  id="bc-time"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
              {frequency === "MONTHLY" && (
                <div className="space-y-2">
                  <Label htmlFor="bc-dom">Day of month</Label>
                  <Input
                    id="bc-dom"
                    type="number"
                    min={1}
                    max={31}
                    value={dayOfMonth}
                    onChange={(e) =>
                      setDayOfMonth(
                        Math.min(31, Math.max(1, Number(e.target.value) || 1)),
                      )
                    }
                  />
                </div>
              )}
            </div>

            {frequency === "WEEKLY" && (
              <div className="space-y-2">
                <Label>On days</Label>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map((d) => (
                    <Button
                      key={d.value}
                      type="button"
                      size="sm"
                      variant={weekdays.includes(d.value) ? "default" : "outline"}
                      className={
                        weekdays.includes(d.value) ? "bg-gray-900 text-white" : ""
                      }
                      onClick={() => toggleWeekday(d.value)}
                    >
                      {d.label}
                    </Button>
                  ))}
                </div>
                {weekdays.length === 0 && (
                  <p className="text-xs text-red-500">Pick at least one day.</p>
                )}
              </div>
            )}

            <div className="max-w-xs space-y-2">
              <Label htmlFor="bc-end">Ends (optional, your local time)</Label>
              <Input
                id="bc-end"
                type="datetime-local"
                min={nowLocal}
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="mt-5">
          <HasAccess permission="write">
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600"
              disabled={!canSubmit}
              onClick={() => setConfirmOpen(true)}
            >
              {sendMode === "RECURRING" ? (
                <Repeat className="mr-2 h-4 w-4" />
              ) : sendMode === "SCHEDULE" ? (
                <Clock className="mr-2 h-4 w-4" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {primaryLabel}
            </Button>
          </HasAccess>
          {!messageReady && (
            <p className="mt-2 text-xs text-gray-400">
              Fill in title, message, type and path to enable sending.
            </p>
          )}
        </div>
      </section>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {sendMode === "NOW"
                ? "Send this broadcast?"
                : sendMode === "SCHEDULE"
                  ? "Schedule this broadcast?"
                  : "Create this recurring broadcast?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1 text-sm text-gray-600">
            {sendMode === "NOW" && (
              <p>
                This will send{" "}
                <span className="font-semibold text-gray-900">“{title}”</span> to
                approximately{" "}
                <span className="font-semibold text-gray-900">
                  {matchedUsers.toLocaleString()}
                </span>{" "}
                matching user{matchedUsers === 1 ? "" : "s"}. This can’t be undone.
              </p>
            )}
            {sendMode === "SCHEDULE" && scheduledAt && (
              <p>
                <span className="font-semibold text-gray-900">“{title}”</span>{" "}
                will send on{" "}
                <span className="font-semibold text-gray-900">
                  {format(new Date(scheduledAt), "PPp")}
                </span>{" "}
                to whoever matches the audience at that time (~
                {matchedUsers.toLocaleString()} now).
              </p>
            )}
            {sendMode === "RECURRING" && (
              <p>
                <span className="font-semibold text-gray-900">“{title}”</span>{" "}
                will send{" "}
                <span className="font-semibold text-gray-900">
                  {describeRecurrence()}
                </span>
                , re-checking the audience each run (~
                {matchedUsers.toLocaleString()} now).
              </p>
            )}
            {activeFilterCount === 0 && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800">
                No filters selected — this targets <strong>all</strong> users.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600"
              disabled={submitting}
              onClick={handleConfirm}
            >
              {submitting
                ? "Working…"
                : sendMode === "NOW"
                  ? "Confirm & send"
                  : "Confirm & schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RecipientsPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        filters={audience}
      />
    </div>
  );
}
